import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HeartPulse, Search, X } from 'lucide-react'
import Avatar from '../../../components/Avatar/Avatar'
import { formatDateTime } from './patientFormat'

function readingLine(patient) {
  const parts = []
  if (patient.reasons?.includes('Febre') && patient.temperature != null) {
    parts.push(`${Number(patient.temperature).toFixed(1)}°C`)
  }
  if (patient.reasons?.includes('SpO2 baixo') && patient.oxygen != null) {
    parts.push(`SpO2 ${patient.oxygen}%`)
  }
  if (patient.reasons?.includes('Pressão alterada')) {
    parts.push(`PA ${patient.systolic ?? '—'}/${patient.diastolic ?? '—'}`)
  }
  if (patient.reasons?.includes('Frequência cardíaca') && patient.heart_rate != null) {
    parts.push(`FC ${patient.heart_rate}`)
  }
  return parts.join(' · ')
}

function AlteredSignsSheet({ open, patients = [], onClose }) {
  const [search, setSearch] = useState('')
  const listRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return undefined
    setSearch('')
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  useEffect(() => {
    const node = listRef.current
    if (!open || !node) return undefined
    let timer
    const mark = () => {
      node.classList.add('is-scrolling')
      window.clearTimeout(timer)
      timer = window.setTimeout(() => node.classList.remove('is-scrolling'), 700)
    }
    node.addEventListener('scroll', mark, { passive: true })
    return () => {
      node.removeEventListener('scroll', mark)
      window.clearTimeout(timer)
    }
  }, [open, patients.length])

  const visible = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt')
    if (!term) return patients
    return patients.filter((patient) => String(patient.name || '').toLocaleLowerCase('pt').includes(term))
  }, [patients, search])

  if (!open) return null

  return createPortal(
    <div className="shift-glance-layer">
      <button type="button" className="shift-glance-overlay" aria-label="Fechar sinais alterados" onClick={onClose} />
      <section className="shift-glance" role="dialog" aria-modal="true" aria-labelledby="altered-glance-title">
        <header className="shift-glance-head">
          <div className="shift-glance-brand">
            <span className="shift-glance-icon" aria-hidden>
              <HeartPulse size={20} />
            </span>
            <div>
              <h2 id="altered-glance-title">Sinais alterados</h2>
              <p>Quem está fora do eixo agora</p>
            </div>
          </div>
          <div className="shift-glance-tools">
            <label className="shift-glance-search">
              <Search size={15} aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar paciente"
                aria-label="Buscar paciente"
              />
            </label>
            <button type="button" className="shift-glance-close" aria-label="Fechar" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </header>
        <div className="shift-glance-list" ref={listRef}>
          {visible.length === 0 && (
            <p className="patients-muted">{search.trim() ? 'Nenhum paciente encontrado.' : 'Nenhum sinal alterado agora.'}</p>
          )}
          {visible.map((patient) => (
            <article key={patient.id} className="shift-glance-row altered-glance-row">
              <div className="shift-glance-patient" title={patient.name}>
                <Avatar name={patient.name} imageUrl={patient.photo_url} size="md" />
                <strong>{patient.name}</strong>
              </div>
              <div className="altered-glance-summary">
                <p>{(patient.reasons || []).join(' · ') || 'Sinal alterado'}</p>
                <span>{readingLine(patient) || 'Sem leitura numérica'}</span>
                <small>
                  {[patient.bed || 'Sem leito', patient.recorded_at ? formatDateTime(patient.recorded_at) : null].filter(Boolean).join(' · ')}
                </small>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>,
    document.body
  )
}

export default AlteredSignsSheet
